/* eslint-disable no-restricted-syntax */
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAuth } from 'hooks/useAuth';
import { appendAiMessage, getAiUsage } from 'modules/ai/redux/aiActions';

type ChatStreamOptions = {
  societyId: string;
  conversationId: string | null;
  message: string;
  onConversationId?: (id: string) => void; // eslint-disable-line no-unused-vars
  onDone?: () => void;
};

type SseEvent = { event: string; data: string };

const parseSseBlock = (block: string): SseEvent | null => {
  const lines = block.split('\n');
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  return { event, data: dataLines.join('\n') };
};

export const useAiChatStream = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth();

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(
    async ({
      societyId,
      conversationId,
      message,
      onConversationId,
      onDone,
    }: ChatStreamOptions) => {
      setError(null);
      setStreamingText('');
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = (await getAccessTokenSilently()) as unknown as
          | string
          | null;
        const baseUrl = process.env.REACT_APP_API_URL_V2 || '';

        const response = await fetch(`${baseUrl}/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ societyId, conversationId, message }),
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 402) {
            const body = await response.json().catch(() => ({}));
            setError('AI_QUOTA_EXCEEDED');
            setIsStreaming(false);
            throw Object.assign(new Error('AI quota exceeded'), {
              code: 'AI_QUOTA_EXCEEDED',
              ...body,
            });
          }
          setError(`HTTP ${response.status}`);
          setIsStreaming(false);
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response has no body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';
        let assistantMessageId = `assistant-${Date.now()}`;

        const dispatchFinalUsage = () => {
          dispatch(getAiUsage(societyId) as any);
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const rawBlock = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const ev = parseSseBlock(rawBlock);
            if (ev) {
              try {
                const payload = JSON.parse(ev.data);
                if (ev.event === 'start' && payload.conversationId) {
                  assistantMessageId = `assistant-${
                    payload.conversationId
                  }-${Date.now()}`;
                  onConversationId?.(payload.conversationId);
                } else if (ev.event === 'chunk' && payload.content) {
                  accumulated += payload.content;
                  setStreamingText(accumulated);
                } else if (ev.event === 'done') {
                  dispatch(
                    appendAiMessage({
                      id: assistantMessageId,
                      role: 'assistant',
                      content: accumulated,
                      createdAt: new Date().toISOString(),
                    })
                  );
                  setStreamingText('');
                  setIsStreaming(false);
                  dispatchFinalUsage();
                  onDone?.();
                } else if (ev.event === 'error') {
                  setError(payload.message || 'Error');
                }
              } catch (parseErr) {
                // ignore non-JSON payloads
              }
            }
            sep = buffer.indexOf('\n\n');
          }
        }

        setIsStreaming(false);
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          setIsStreaming(false);
          return;
        }
        setIsStreaming(false);
        if (!error) setError(err?.message || 'Streaming failed');
        throw err;
      } finally {
        abortRef.current = null;
      }
    },
    [dispatch, error, getAccessTokenSilently]
  );

  return {
    sendMessage,
    cancel,
    isStreaming,
    streamingText,
    error,
  };
};
